import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { DefaultQuoteItemModel, DefaultQuoteItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['defaultQuoteItems']

type DefaultQuoteItemFilters = {
  string?: string,
  owner_id?: number,
  owner_type?: 'Agency',
}

type UseDefaultQuoteItemsOptions = {
  filters?: DefaultQuoteItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: DefaultQuoteItemRequestOptions,
}

function useDefaultQuoteItems(options: UseDefaultQuoteItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    string: filterString,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
  } = filters

  const {
    updatedEntities: { defaultQuoteItems: defaultQuoteItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { defaultQuoteItems } = useSelector(reduxState => reduxState.entities)

  const filteredDefaultQuoteItems = useMemo(() => {
    const filtered = Object.values(defaultQuoteItems).filter((defaultQuoteItem: DefaultQuoteItemModel) => {
      const {
        id,
        title,
        owner_id,
        owner_type,
      } = defaultQuoteItem

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle
      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = matchFilterString(owner_type, filterOwnerType, { exact: true })

      return matchOwnerId && matchOwnerType && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [defaultQuoteItemsUpdatedAt, JSON.stringify(filters)])

  const filteredDefaultQuoteItemsCount = filteredDefaultQuoteItems.length
  const hasFilteredDefaultQuoteItems = !!filteredDefaultQuoteItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredDefaultQuoteItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingDefaultQuoteItems } = useReduxAction(
    'defaultQuoteItems',
    'loadDefaultQuoteItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredDefaultQuoteItems,
    filteredDefaultQuoteItemsCount,
    hasFilteredDefaultQuoteItems,
    loading: loadingDefaultQuoteItems,
  }
}

export default useDefaultQuoteItems
